/**
 * Review-share gating
 * ===================
 *
 * The client's policy: spots must pass technical QC BEFORE they can be shared
 * for review. If a reviewed spot later fails QC, it would have to be re-shared
 * — which is bad UX for reviewers. Enforcing QC-first prevents that loop.
 *
 * The org-level toggle `settings.bypassQcBeforeReview` (default false = rule
 * is enforced) lets a Master Org admin opt out per org if a different
 * workflow is needed. See DeliveryOverridesEditor.
 *
 * Both the single-spot share (FindAudio Share for Review) and the campaign
 * batch share (JobDetail Share For Review) go through these helpers before
 * opening their respective modals.
 */

/**
 * A track is allowed to be shared for review when:
 *   - the org is bypassing the QC-before-Review rule, OR
 *   - the track's `qc_status` is exactly `'pass'`.
 *
 * Anything else (`'fail'`, `'testing'`, `undefined`) blocks the share so the
 * user runs QC first.
 */
export function canShareForReview(
  track: { qc_status?: string | null } | null | undefined,
  opts: { bypassQcBeforeReview: boolean },
): { ok: boolean; reason?: string } {
  if (opts.bypassQcBeforeReview) return { ok: true };
  if (!track) return { ok: false, reason: 'No track selected.' };
  if (track.qc_status === 'pass') return { ok: true };
  return {
    ok: false,
    reason: "This spot hasn't passed QC yet. Run QC before sharing for review.",
  };
}

/**
 * Batch variant — every track must individually pass `canShareForReview`. If
 * any don't, returns the count and a message naming the first few offenders
 * so the toast can guide the user to the right spots.
 */
export function canShareBatchForReview(
  tracks: Array<{ trackName?: string; qc_status?: string | null }>,
  opts: { bypassQcBeforeReview: boolean },
): { ok: boolean; reason?: string; failingCount?: number } {
  if (opts.bypassQcBeforeReview) return { ok: true };
  const failing = tracks.filter((t) => t?.qc_status !== 'pass');
  if (failing.length === 0) return { ok: true };
  const sample = failing
    .slice(0, 3)
    .map((t) => t.trackName || '(unnamed)')
    .join(', ');
  const more = failing.length > 3 ? ` and ${failing.length - 3} more` : '';
  return {
    ok: false,
    failingCount: failing.length,
    reason: `${failing.length} spot${failing.length === 1 ? '' : 's'} haven't passed QC yet: ${sample}${more}. Run Batch QC first.`,
  };
}
